import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { environment } from 'environments/environment';
import {
  NgcCookieConsentService,
  NgcInitializeEvent,
  NgcNoCookieLawEvent,
  NgcStatusChangeEvent,
} from 'ngx-cookieconsent';
import { Subscription, take } from 'rxjs';
import { UserService } from './core/user/user.service';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { AuthService } from './core/auth/auth.service';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { User } from './core/user/user.types';
import { MessageNotification } from './modules/models/messageNotification.type';
import { decrypt, encrypt, isSuperAdmin, fetchCompanyBySiret } from './shared/utils';
import { Router, NavigationEnd } from '@angular/router';
import { HealthService } from './shared/services/health.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SignedNotificationComponent } from './shared/signed-notification/signed-notification.component';
import { User_UserProfile } from './modules/models/profile-management/userProfile.type';
import { selectIsPartnerAdmin, selectUserCustomer, selectUserPartner, selectIsAcepPartner, selectIsMandataire } from './ngrx/selectors/user.selectors';
import { UserState } from './ngrx/states/userState.state';
import { Store } from '@ngrx/store';
import { setIsAcepPartner, setIsMandataire, setIsPartnerAdmin, setUserCustomer, setUserPartner } from './ngrx/actions/user.action';
import { setCurrentCompany } from './ngrx/actions/company.actions';
import { selectCurrenteCompany } from './ngrx/selectors/company.selectors';
import { BroadcastService } from './broadcast.service';
import { ClarityService } from './shared/services/clarity.service';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { StorageItemService } from "./shared/services/storageItem.service";
import { CustomerService } from './modules/customer-details/customer.service';
import { ActorService } from './shared/services/actor.service';
import { ESigantureService } from './shared/services/e-siganture.service';
import { CompanyService } from 'app/shared/services/companyservice.service';
import { Actor } from './modules/models/actor.type';

declare global {
  interface Window { clarity?: any; }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private popupOpenSubscription: Subscription;
  private popupCloseSubscription: Subscription;
  private initializeSubscription: Subscription;
  private statusChangeSubscription: Subscription;
  private revokeChoiceSubscription: Subscription;
  private noCookieLawSubscription: Subscription;
  private routerSubscription: Subscription;
  private hubConnectionBuilder!: HubConnection;
  private suppressConsentOnce: boolean = false; // used when navigating via "En savoir plus"
  private showRevokeOnInit = false;
  connectedUser!: User;
  mouseX: number;
  mouseY: number;
  currentCompanyId: string;

  constructor(
    private ccService: NgcCookieConsentService,
    private _userService: UserService,
    private _authService: AuthService,
    private _healthService: HealthService,
    private _fuseConfirmationService: FuseConfirmationService,
    private _router: Router,
    private _snackBar: MatSnackBar,
    private store: Store<UserState>,
    private broadcastService: BroadcastService,
    private _storageService: StorageItemService,
    private clarityService: ClarityService,
    private _customerService: CustomerService,
    private _actorService: ActorService,
    private _esignatureService: ESigantureService,
    private _companyService: CompanyService,
  ) { }
  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: Event) {
    // Perform your cleanup logic here
    this.store.select(selectUserPartner).subscribe(res => {
      if (res)
        localStorage.setItem('partner', encrypt(JSON.stringify(res)));
    })
    this.store.select(selectIsPartnerAdmin).subscribe(res => {
      if (res)
        localStorage.setItem('isAdmin', encrypt(JSON.stringify(res)));
    });
    this.store.select(selectIsMandataire).subscribe(res => {
      if (res)
        localStorage.setItem('isMandataire', encrypt(JSON.stringify(res)));
    });
    this.store.select(selectIsAcepPartner).subscribe(res => {
      if (res)
        localStorage.setItem('isAcepPartner', encrypt(JSON.stringify(res)));
    });
    this.store.select(selectUserCustomer).subscribe(res => {
      if (res)
        localStorage.setItem('customer', encrypt(JSON.stringify(res)));
    });
    this.store.select(selectCurrenteCompany).subscribe(res => {
      if (res)
        localStorage.setItem('company', encrypt(JSON.stringify(res)));
    })
  }
  ngOnInit() {
    // Only load Clarity if both cookies are accepted and analytics cookies are enabled (default true if unset)
    const cookiesAccepted = localStorage.getItem('cookiesAccepted') === 'true';
    const storedPref = localStorage.getItem('analyticsCookiesEnabled');
    const analyticsEnabled = (storedPref === null) ? true : (storedPref === 'true');
    if (cookiesAccepted && analyticsEnabled) {
      this.clarityService.load()
        .then(() => console.log('✅ Microsoft Clarity loaded'))
        .catch(err => console.error('Failed to load Clarity script', err));
    } else {
      console.log('ℹ️ Clarity not loaded (analytics cookies disabled or no consent)');
    }
    this.broadcastService.requestState();
    const partner = localStorage.getItem('partner');
    if (partner) {
      this.store.dispatch(setUserPartner({ userPartner: JSON.parse(decrypt(partner)) }));
      localStorage.removeItem('partner');
    }
    const customer = localStorage.getItem('customer');
    if (customer) {
      this.store.dispatch(setUserCustomer({ userCustomer: JSON.parse(decrypt(customer)) }));
      localStorage.removeItem('customer');
    }
    const company = localStorage.getItem('company');
    if (company) {
      this.store.dispatch(setCurrentCompany({ company: JSON.parse(decrypt(company)) }));
      localStorage.removeItem('company');
    }
    const isAdmin = localStorage.getItem('isAdmin');
    if (isAdmin) {
      this.store.dispatch(setIsPartnerAdmin({ isPartnerAdmin: JSON.parse(decrypt(isAdmin)) }));
      localStorage.removeItem('isAdmin');
    }
    const isMandataire = localStorage.getItem('isMandataire');
    if (isMandataire) {
      this.store.dispatch(setIsMandataire({ isMandataire: JSON.parse(decrypt(isMandataire)) }));
      localStorage.removeItem('isMandataire');
    }
    const isAcepPartner = localStorage.getItem('isAcepPartner');
    if (isAcepPartner) {
      this.store.dispatch(setIsAcepPartner({ isAcepPartner: JSON.parse(decrypt(isAcepPartner)) }));
      localStorage.removeItem('isAcepPartner');
    }
    let coockieState = localStorage.getItem('accept-coockies');
    this.showRevokeOnInit = coockieState === 'true';

    this._userService.connectedUser$.subscribe((user: User) => {
      if (user) {
        this.connectedUser = user;
        if (!this.connectedUser?.cguAccepted && !this.connectedUser?.isDeleted) {
          this._router.navigate(['/cgu']);
        }
      }
    });

    let currentPartner = localStorage.getItem('currentPartner_localstorage') ? JSON.parse(localStorage.getItem('currentPartner_localstorage')) : null;
    if (currentPartner) {
      fetchCompanyBySiret(this._companyService, this._snackBar, currentPartner.siret, currentPartner.code).subscribe(company => {
        this.currentCompanyId = company?.id;
      });
    }

    if (coockieState !== 'true') {
      this.ccService.fadeIn();
    } else {
      this.ccService.fadeOut();
    }

    this.toggleRevoke(this.showRevokeOnInit);

    // subscribe to cookieconsent observables to react to main events
    this.popupOpenSubscription = this.ccService.popupOpen$.subscribe(() => {
      // you can use this.ccService.getConfig() to do stuff...
    });

    this.popupCloseSubscription = this.ccService.popupClose$.subscribe(() => {
      // you can use this.ccService.getConfig() to do stuff...
    });

    this.initializeSubscription = this.ccService.initialize$.subscribe(
      (event: NgcInitializeEvent) => {
        const hasRecordedConsent = localStorage.getItem('accept-coockies') === 'true';
        this.showRevokeOnInit = hasRecordedConsent;
        this.toggleRevoke(hasRecordedConsent);
      }
    );

    this.statusChangeSubscription = this.ccService.statusChange$.subscribe(
      (event: NgcStatusChangeEvent) => {
        // you can use this.ccService.getConfig() to do stuff...
        // If we just navigated to policy via "En savoir plus", ignore this event
        if (this.suppressConsentOnce) {
          this.suppressConsentOnce = false;
          return;
        }
        const answered = event.status === 'allow' || event.status === 'deny';
        if (answered) {
          localStorage.setItem('accept-coockies', 'true');
        } else {
          localStorage.removeItem('accept-coockies');
        }

        this.showRevokeOnInit = answered;
        this.toggleRevoke(answered);

        if (event.status === 'allow') {
          localStorage.setItem('analyticsCookiesEnabled', 'true');
          localStorage.setItem('cookiesAccepted', 'true');
          this.clarityService.tryConsent('granted');
          this.clarityService.load()
            .then(() => this.clarityService.tryConsent('granted'))
            .catch(err => console.error('Failed to load Clarity script', err));
        } else if (event.status === 'deny') {
          // Persist analytics refusal
          localStorage.setItem('analyticsCookiesEnabled', 'false');
          localStorage.setItem('cookiesAccepted', 'false');
          this.clarityService.tryConsent('denied');
        }
      }
    );

    this.revokeChoiceSubscription = this.ccService.revokeChoice$.subscribe(() => {
      localStorage.removeItem('accept-coockies');
      localStorage.removeItem('analyticsCookiesEnabled');
      localStorage.removeItem('cookiesAccepted');
      this.showRevokeOnInit = false;
      this.toggleRevoke(false);
    });

    this.noCookieLawSubscription = this.ccService.noCookieLaw$.subscribe(
      (event: NgcNoCookieLawEvent) => {
        // you can use this.ccService.getConfig() to do stuff...
      }
    );

    // Close cookie banner when navigating to the cookies policy page via "En savoir plus"
    this.routerSubscription = this._router.events.subscribe(evt => {
      if (evt instanceof NavigationEnd) {
        const url = evt.urlAfterRedirects || (evt as any).url;
        if (url && url.startsWith('/cookies-rules')) {
          try {
            // Do not record any consent choice when opening the policy page
            this.suppressConsentOnce = true;
            this.ccService.fadeOut();
            this.ccService.destroy();
          } catch { }
        }
      }
    });

    //this.trackMatomo();

    let urlSignalR = environment.RESOURCE_API + 'notify';
    this.hubConnectionBuilder = new HubConnectionBuilder()
      .withUrl(urlSignalR)
      .withAutomaticReconnect()
      .build();

    this.hubConnectionBuilder
      .start()
      .then(() => console.log('Connection started.......!'))
      .catch(err => console.log('Error while connect with server: ' + err));

    this.hubConnectionBuilder.onclose(() => {
      console.log('Connection closed.......!');
      this.hubConnectionBuilder.start().then(() => console.log('Connection started.......!'));
    });

    this.hubConnectionBuilder.on('BroadcastMessage', (result: MessageNotification) => {
      console.log('BroadcastMessage', result.type);

      switch (result.type) {
        case 'RoleUpdate':
          this.handleRoleChange(result);
          break;
        case 'SupportRequest':
          this.handleSupportRequest(result);
          break;
        case 'DeactivateUser':
          this.handleUserDeactivation(result);
          break;
        case 'DeactivateOrganisation':
          this.handleOrganisationDeactivation(result);
          break;
        case 'DocusignESignature':
          this.handleDocusignESignature(result);
          this.handleSicaNotification(result);
          break;
        case 'DropComplete':
          this.DropComplete(result);
          this.handleSicaNotification(result);
          break;
        case 'DeleteUser':
          this.handleUserDeactivation(result);
          break;
        case 'ExtractComplete':
          this.handleExtractComplete(result);
          break;
        case 'DropMultipleComplete':
          this.DropMultipleComplete(result);
          this.handleSicaNotification(result);
          break;
        case 'DossierSubmittedComplete':
          this.handleSubmitDossierComplete(result);
          break;
        case 'UpdateActor':
          this.handleUpdateActor(result);
          break;
        case 'DocumentDraftProcessedComplete':
          this.handleDocumentDraftProcessedComplete(result);
          break;
        case 'DraftDocumentProgressBarResponse':
          this.handleDraftDocumentProgressBarResponse(result);
          break;
        case 'DraftProcessing':
          this.handleDraftProcessing(result);
          break;
        default:
          this.handleSicaNotification(result);
          break;
      }
    });
  }




  DropMultipleComplete(result: MessageNotification) {
    if (result.companyId === this.connectedUser?.companyId || this.isPartnerAdmin(this.connectedUser?.user_UserProfiles)) {
      const contentArray = JSON.parse(result.content);
      if (contentArray.length > 0) {
        var parsedDistinctDossiers = this.groupByDropRefAndName(contentArray);
        const fileName = contentArray[0]?.ZipFileName;
        const cleanName = fileName ? fileName.replace(/\.zip$/i, "") : undefined;
        this.exportExcel(parsedDistinctDossiers, cleanName + '.xlsx');
      }
    }
  }

  exportExcel(dossiers: any[], fileName: string = 'dossiers.xlsx'): void {
    // Prepare data rows
    const worksheetData = dossiers.map(d => ({
      'Id Dossier': d.dropReference,
      'Nom Dossier': d.name
    }));

    // Create worksheet and workbook
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook: XLSX.WorkBook = {
      Sheets: { 'Dossiers': worksheet },
      SheetNames: ['Dossiers']
    };

    // Write and save the Excel file
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    FileSaver.saveAs(blob, fileName);
  }

  handleExtractComplete(result: MessageNotification) {
    let email = JSON.parse(result.content).email;
    let fileUrl = JSON.parse(result.content).path;
    const parts = fileUrl.split("/");
    const folderId = parts[1];
    const fileName = parts[2];
    const fileType = fileName.split(".").pop();
    if (email === this.connectedUser?.email) {
      this._storageService.getAcepStorageItemByNodeId(folderId, fileName).subscribe({
        next: (fileBlob) => {
          var base64Format = fileBlob.content;
          this.downloadFile(base64Format, fileType, fileName);


          this._snackBar.open('Extraction des dossiers terminée ', null, {
            duration: 5000,
            panelClass: 'accent-snackbar'
          });

        },
        error: (err) => {
          console.error('Error downloading file:', err);
        }
      });
    }
  }

  handleSubmitDossierComplete(result: MessageNotification) {
    let currentCustomer = localStorage.getItem('currentCustomer');
    let customerId = null;

    if (currentCustomer) {
      customerId = JSON.parse(currentCustomer).id;
    }

    if (result.customerId == customerId) {
      this._customerService.dossierSubmittedInformations(result.customerId).subscribe({
        next: (dossierInfo) => {
          console.log('Dossier submitted information updated');
          this._customerService.dossierSubmittedInformation = dossierInfo;
        },
        error: (err) => {
          console.error('Error fetching dossier submitted information:');
        }
      });
    }
  }

  handleUpdateActor(result: MessageNotification) {
    try {
      const content = JSON.parse(result?.content || '{}');
      const actorId = content.acteurId ?? content.actorId ?? content.id;

      // Only emit when we have an actor id and the flag is present
      if (actorId) {
        const actor: Actor = {
          id: actorId,
          actorType: content.actorType ?? '',
          firstName: content.firstName ?? '',
          lastName: content.lastName ?? '',
          fullName: content.fullName ?? '',
          email: content.email ?? '',
          phone: content.phone ?? '',
        };

        this._actorService.actorUpdatedInformation = actor;
      }
    } catch (e) {
      console.error('Failed to parse UpdateActor message', e, result?.content);
    }
  }

  downloadFile(base64File, extension, fileName): void {
    const byteCharacters = atob(base64File);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/' + extension });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style.display = 'none';
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  checkHealth() {
    let unhealthCount = 0;
    setInterval(() => {
      this._healthService.getHealth().subscribe({
        next: (result) => {
          switch (result.status) {
            case 'Unhealthy':
              unhealthCount++;
              if (unhealthCount === 3) {
                this._router.navigate(['/error-page']);
                unhealthCount = 0; // Reset the counter after navigating
              }
              break;
            case 'Healthy':
              unhealthCount = 0; // Reset the counter if the status is healthy
              if (this._router.url === '/error-page') {
                this._router.navigate(['/sign-in']);
              }
              break;
          }
        },
        error: () => {
          this._router.navigate(['/error-page']);
        }
      });
    }, 15000);
  }

  private handleRoleChange(result: MessageNotification): void {
    if (result.content === this.connectedUser?.id || this.checkProfile(result.content)) {
      this.showConfirmation(
        'Changement de rôle',
        'Votre niveau de permission a été modifié. Pour des soucis de sécurité, vous allez être déconnecté pour mettre à jour votre profil.',
        'Se déconnecter',
        () => this.handleSignOutAndReload()
      );
    }
  }

  checkProfile(content: string): boolean {
    return this.connectedUser?.user_UserProfiles?.some(up => up.userProfileId === content) || false;
  }

  private handleOrganisationDeactivation(result: MessageNotification): void {
    if (result.content === this.connectedUser?.companyId) {
      this.showConfirmation(
        'Actualisation requise',
        "Des modifications ont été apportées à votre compte. Dans le cadre de ces ajustements et pour garantir la sécurité, une déconnexion est nécessaire pour actualiser vos informations d'accès.",
        'Se déconnecter',
        () => this.handleSignOutAndReload()
      );
    }
  }

  private handleSupportRequest(result: MessageNotification): void {
    if (isSuperAdmin()) {
      this.showConfirmation(
        'Nouvelle demande de support',
        'Une nouvelle demande de support a été ajoutée par un utilisateur. Veuillez consulter la liste des demandes de support.',
        'Consulter',
        () => this._router.navigate(['/settings/support-requests'])
      );
    }
  }

  private handleDraftDocumentProgressBarResponse(result: MessageNotification): void {
    if (!this.isDocumentDraftForConnectedUser(result, this.hasDocumentDraftProjectId(result))) {
      return;
    }

    this.broadcastService.emitDocumentDraftProgress(result);
  }

  private handleDraftProcessing(result: MessageNotification): void {
    if (!this.hasDocumentDraftProjectId(result)) {
      return;
    }

    this.broadcastService.emitDocumentDraftProgress(result);
  }

  private handleDocumentDraftProcessedComplete(result: MessageNotification): void {
    if (!this.isDocumentDraftForConnectedUser(result)) {
      return;
    }

    this.broadcastService.emitDocumentDraftProcessed(result);
  }

  private isDocumentDraftForConnectedUser(result: MessageNotification, allowMissingUserId = false): boolean {
    const connectedUserId = (this.connectedUser as any)?.id || (this.connectedUser as any)?.Id || (this.connectedUser as any)?.guidId;
    if (!connectedUserId) {
      return false;
    }

    // Prefer userId from the JSON payload in content.
    let contentUserId: string | null = null;
    try {
      const parsedContent = result?.content ? JSON.parse(result.content) : null;
      contentUserId = parsedContent?.userId || parsedContent?.UserId || null;
    } catch {
      contentUserId = null;
    }

    // Fallback to root-level userId when content does not include it.
    const notificationUserId = contentUserId || (result as any)?.userId || (result as any)?.UserId || null;
    if (!notificationUserId) {
      return allowMissingUserId;
    }

    return !!notificationUserId && notificationUserId === connectedUserId;
  }

  private hasDocumentDraftProjectId(result: MessageNotification): boolean {
    try {
      const parsedContent = result?.content ? JSON.parse(result.content) : null;
      return !!(parsedContent?.projectId || parsedContent?.ProjectId);
    } catch {
      return false;
    }
  }

  private handleUserDeactivation(result: MessageNotification): void {
    if (result.content === this.connectedUser?.email) {
      this.showConfirmation(
        'Actualisation requise',
        "Des modifications ont été apportées à votre compte. Dans le cadre de ces ajustements et pour garantir la sécurité, une déconnexion est nécessaire pour actualiser vos informations d'accès.",
        'Se déconnecter',
        () => this.handleSignOutAndReload()
      );
    }
  }

  private toggleRevoke(show: boolean): void {
    try {
      this.ccService.toggleRevokeButton(show);
    } catch (error) {
    }
  }

  private DropComplete(result: MessageNotification): void {
    if (result.companyId === this.connectedUser?.companyId || this.isPartnerAdmin(this.connectedUser?.user_UserProfiles)) {
      this._snackBar.open('Demande enregistrée avec succès sous la référence ' + result.content, null, {
        duration: 3000,
        panelClass: 'accent-snackbar'
      }).onAction().subscribe(() => {
        // Add your action here
        this._router.navigate(['/partners/drop-list']);
      });
    }
  }

  isPartnerAdmin(userUserProfiles: User_UserProfile[]): boolean {
    return userUserProfiles?.some(
      profile =>
        profile?.userProfile?.name === 'Admin E. Partenaire'
    );
  }

  handleDocusignESignature(result: MessageNotification): void {
    let content = JSON.parse(result.content);
    let customer = result.customer;

    if (content && (result.companyId === this.connectedUser?.companyId || result.companyId == this.currentCompanyId)
      && (content.Status === 'envelope-completed'
        || content.Status === 'envelope-voided'
        || content.Status === 'envelope-removed'
        || content.Status === 'envelope-discard'
        || content.Status === 'envelope-deleted'
        || content.Status === 'envelope-purge'
        || content.Status === 'envelope-rejected'
        || content.Status === 'envelope-declined')) {
      this._esignatureService.docusignNotification = { content, customer };
      this._snackBar.openFromComponent(SignedNotificationComponent, {
        data: {
          content: content,
          customer: customer,
        },
        duration: 10000,
        panelClass: 'esign-snackbar'
      });

    }

  }

  private handleSignOutAndReload(): void {
    // Sign out
    this._authService.signOut();
    // Reload the app
    location.reload();
  }

  private showConfirmation(title: string, message: string, confirmLabel: string, confirmAction: () => void): void {
    const dialogRef = this._fuseConfirmationService.open({
      title: title,
      message: message,
      actions: {
        confirm: { label: confirmLabel },
        cancel: { show: false, label: 'Annuler' }
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'confirmed') {
        confirmAction();
      }
    });
  }

  ngOnDestroy() {
    // unsubscribe to cookieconsent observables to prevent memory leaks
    this.popupOpenSubscription.unsubscribe();
    this.popupCloseSubscription.unsubscribe();
    this.initializeSubscription.unsubscribe();
    this.statusChangeSubscription.unsubscribe();
    this.revokeChoiceSubscription.unsubscribe();
    this.noCookieLawSubscription.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }
  trackMatomo(): void {
    var _paq = ((window as any)._paq = (window as any)._paq || []);
    /* tracker methods like "setCustomDimension" should be called before "trackPageView" */
    _paq.push(['trackPageView']);
    _paq.push(['enableLinkTracking']);
    (function () {
      var u = '//aceenergie.dyndns.org:8088/';
      _paq.push(['setTrackerUrl', u + 'matomo.php']);
      _paq.push(['setSiteId', environment.MATOMO_APP_ID]);
      var d = document,
        g = d.createElement('script'),
        s = d.getElementsByTagName('script')[0];
      g.async = true;
      g.src = u + 'matomo.js';
      s.parentNode.insertBefore(g, s);
    })();
  }
  groupByDropRefAndName(items: InputItem[]): GroupedDossier[] {
    const uniqueMap = new Map<string, GroupedDossier>();

    for (const item of items) {
      // Key ensures uniqueness for each combination
      const key = `${item.DropReference}||${item.Name}||${item.Id}`;
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, {
          dropReference: item.DropReference,
          name: item.Name,
          folderId: item.Id,
        });
      }
    }

    return Array.from(uniqueMap.values());
  }

  private handleSicaNotification(result: MessageNotification): void {
    const companyIdInMessage = result?.companyId ?? null;

    if (!companyIdInMessage) {
      this.broadcastService.emitSicaNotification(result);
      return;
    }

    this.store.select(selectCurrenteCompany).pipe(take(1)).subscribe({
      next: (currentCompany: any) => {
        const currentCompanyId = this.currentCompanyId ?? null;

        if (String(currentCompanyId) === String(companyIdInMessage)) {
          this.broadcastService.emitSicaNotification(result);
        }
      },
      error: () => {
        console.error('Failed to get current company from store');
      }
    });
  }
}

export interface InputItem {
  Name: string,
  DropReference: string,
  Id: string
};

export interface GroupedDossier {
  dropReference: string,
  name: string,
  folderId: string
};

